import { Component, effect, inject, signal, Input } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonsModule, ChipModule } from '@progress/kendo-angular-buttons';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { DialogCloseResult, DialogModule, DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import {
  AddEvent,
  CancelEvent,
  CellClickEvent,
  EditEvent,
  GridComponent,
  GridModule,
  RemoveEvent,
  SaveEvent,
} from '@progress/kendo-angular-grid';
import { IconsModule, SVGIcon } from '@progress/kendo-angular-icons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { ListViewModule } from '@progress/kendo-angular-listview';
import { NavigationModule } from '@progress/kendo-angular-navigation';
import { OrgAppbarComponent } from '../../../../../layout/components/org-appbar/org-appbar.component';
import { BaseComponent } from '../../../../../../../core/shared/common/base.component';
import { IrcContactRequest, IrcContactService } from './services/soccomms.service';
import { Contact, timezones } from '../../../../../../../core/models/entities';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, filter, Observable, switchMap } from 'rxjs';
import { ContactForm, ContactFormService } from './services/contact-form.service';
import { OrganizationService } from '../../../../services/organization.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { infoCircleIcon } from '@progress/kendo-svg-icons';

@Component({
  selector: 'app-calltree',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LayoutModule,
    IconsModule,
    InputsModule,
    LabelModule,
    ButtonsModule,
    DropDownsModule,
    NavigationModule,
    ReactiveFormsModule,
    DateInputsModule,
    GridModule,
    OrgAppbarComponent,
    ListViewModule,
    DropDownsModule,
    DialogModule,
    ChipModule,
  ],
  templateUrl: './calltree.component.html',
  styleUrl: './calltree.component.scss',
})
export class CalltreeComponent extends BaseComponent {
  public orgService = inject(OrganizationService);
  public ircContactService = inject(IrcContactService);
  private route = inject(ActivatedRoute);
  private formService = inject(ContactFormService);
  private dialogService = inject(DialogService);
  public timeZones = timezones;
  public formGroup: FormGroup<ContactForm> | undefined;
  public contacts = signal<Contact[]>([]);
  public weekdayMap = ['M', 'T', 'W', 'R', 'F', 'S', 'U'];
  public dayFullNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  public dayAbbreviations = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  public dayOptions = [
    { text: 'Monday', value: 1 },
    { text: 'Tuesday', value: 2 },
    { text: 'Wednesday', value: 3 },
    { text: 'Thursday', value: 4 },
    { text: 'Friday', value: 5 },
    { text: 'Saturday', value: 6 },
    { text: 'Sunday', value: 7 },
  ];
  private editedRowIndex: number | undefined;
  private id = 0;
  public initLevel: number | undefined;
  public infoCircleIcon: SVGIcon = infoCircleIcon;
  @Input() showTooltip: boolean = true;

  public initLevels = [
    { text: 'Critical', value: 3 },
    { text: 'High', value: 2 },
    { text: 'Medium', value: 1 },
  ];

  constructor() {
    super();
    this.initEffect();
  }

  /**
   * Formats a phone number for display
   * Handles both international and US formats
   */
  public formatPhoneNumber(phone: string | null): string {
    if (!phone) return '';

    // Clean the phone number first (keep only + and digits)
    const cleaned = phone.replace(/[^\d+]/g, '');

    // Format US numbers with country code (+1)
    if (cleaned.startsWith('+1')) {
      // For +1 (US/Canada), we expect a total of 11 characters (+1 followed by 10 digits)
      if (cleaned.length >= 11) {
        // Extract the 10 digits after the +1 prefix
        const tenDigits = cleaned.substring(2); // Skip the +1 prefix
        const areaCode = tenDigits.substring(0, 3);
        const prefix = tenDigits.substring(3, 6);
        const lineNum = tenDigits.substring(6);
        return `+1(${areaCode})${prefix}-${lineNum}`;
      }
      return cleaned; // Return as is if not enough digits
    }

    // Format other international numbers
    if (cleaned.startsWith('+')) {
      // Extract country code (everything between + and first digit group)
      const countryCodeMatch = cleaned.match(/^\+(\d+)/);
      if (!countryCodeMatch) return cleaned;

      const countryCode = countryCodeMatch[1];
      const nationalNumber = cleaned.substring(countryCode.length + 1); // +1 for the + symbol

      // If we have enough digits, format in groups
      if (nationalNumber.length > 6) {
        return `+${countryCode}${nationalNumber.substring(0, 3)}-${nationalNumber.substring(3)}`;
      }

      return `+${countryCode}${nationalNumber}`;
    }

    // Format 10-digit US number without country code
    if (cleaned.length === 10) {
      const areaCode = cleaned.substring(0, 3);
      const prefix = cleaned.substring(3, 6);
      const lineNum = cleaned.substring(6);
      return `(${areaCode})${prefix}-${lineNum}`;
    }

    // Return as is for any other format
    return cleaned;
  }

  private initEffect() {
    effect(
      () => {
        this.route.queryParams
          .pipe(
            filter((params) => params['organizationId']),
            switchMap((params) => {
              this.id = parseInt(params['organizationId'], 10);
              // Fetch organization details
              return this.orgService.getOrganizationById(this.id).pipe(
                switchMap((organization) => {
                  const initLevelItem = this.initLevels.find(
                    (level) => level.value == organization.irc_initialization_level
                  );
                  this.initLevel = initLevelItem ? initLevelItem.value : undefined;
                  return this.ircContactService.getContacts(this.id);
                })
              );
            })
          )
          .subscribe((data) => {
            this.contacts.set((data.value || []).sort((a, b) => (a.priority_order ?? 0) - (b.priority_order ?? 0)));
          });
      },
      { allowSignalWrites: true }
    );
  }

  public onInitLevelChange(newValue: number) {
    if (this.id) {
      this.orgService.updateInitializationLevel(this.id, newValue).subscribe({
        next: (updatedOrg) => {
          const initLevelItem = this.initLevels.find((level) => level.value == updatedOrg.irc_initialization_level);
          this.initLevel = initLevelItem ? initLevelItem.value : undefined;
        },
        error: (err) => {
          console.error('Failed to update initialization level', err);
        },
      });
    }
  }

  public addHandler(args: AddEvent): void {
    this.closeEditor(args.sender);
    this.formGroup = this.formService.createContactForm();

    // Pre-populate Monday through Sunday (values 1-7) for new contacts
    this.formGroup.controls.working_days.setValue([1, 2, 3, 4, 5, 6, 7]);

    args.sender.addRow(this.formGroup);
  }

  private closeEditor(grid: GridComponent, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }

  onAvailabilityChange(event: Date, formControl: FormControl): void {
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    };
    const militaryTimeString = event.toLocaleTimeString('en-GB', options);
    formControl.setValue(militaryTimeString);
  }

  onDaysChange(event: number[]): void {
    this.formGroup?.controls.working_days.setValue(event);
  }

  saveHandler($event: SaveEvent) {
    if (this.formGroup) {
      const formValue = this.formGroup.value;

      const formatToMilitaryTime = (date: Date | null): string | null => {
        if (!date) {
          return null;
        }
        const options: Intl.DateTimeFormatOptions = {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        };
        return date.toLocaleTimeString('en-GB', options);
      };

      // Normalize phone number to store consistently in the database
      const normalizePhoneNumber = (phone: string | null | undefined): string | null => {
        if (!phone) return null;

        // Clean up the phone number, keeping only the + sign and digits
        const cleaned = phone.replace(/[^\d+]/g, '');

        // If it already has a + sign, return as is
        if (cleaned.startsWith('+')) {
          return cleaned;
        }

        // For 10-digit US numbers, add +1 prefix
        if (cleaned.length === 10) {
          return `+1${cleaned}`;
        }

        // For 11-digit numbers starting with 1, add + prefix
        if (cleaned.length === 11 && cleaned.startsWith('1')) {
          return `+${cleaned}`;
        }

        // For all other numbers, add + prefix if missing
        return cleaned.length > 0 ? `+${cleaned}` : null;
      };

      const contact = {
        ...formValue,
        available_from: formValue.available_from ? formatToMilitaryTime(formValue.available_from) : null,
        available_to: formValue.available_to ? formatToMilitaryTime(formValue.available_to) : null,
        phone: normalizePhoneNumber(formValue.phone),
      };

      const payload: IrcContactRequest = {
        organization_id: this.id,
        priority_order: $event.isNew
          ? Math.max(...this.contacts().map((c) => c.priority_order ?? 0), 0) + 1
          : this.contacts()[$event.rowIndex].priority_order ?? 0,
        phone: contact.phone!,
        extension: contact.extension!,
        name: contact.name!,
        dispatch_line: contact.dispatch_line!,
        working_days: contact.working_days!,
        available_from: contact.available_from,
        available_to: contact.available_to,
        timezone: contact.timezone!,
      };

      if ($event.isNew) {
        this.ircContactService
          .createContact(payload)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.closeEditor($event.sender, $event.rowIndex);
              this.ircContactService.getContacts(this.id).subscribe((data) => {
                this.contacts.set((data.value || []).sort((a, b) => (a.priority_order ?? 0) - (b.priority_order ?? 0)));
              });
            },
            error: (error) => {
              console.error('error', error);
            },
          });
      } else {
        const pk = this.contacts()[$event.rowIndex].pk;
        const updatePayload: IrcContactRequest = {
          ...payload,
          pk: pk,
        };

        this.ircContactService
          .updateContact(updatePayload)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.closeEditor($event.sender, $event.rowIndex);
              this.ircContactService.getContacts(this.id).subscribe((data) => {
                this.contacts.set((data.value || []).sort((a, b) => (a.priority_order ?? 0) - (b.priority_order ?? 0)));
              });
            },
          });
      }
    }
  }

  cancelHandler($event: CancelEvent) {
    this.closeEditor($event.sender, $event.rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }

  cellClickHandler($event: CellClickEvent) {
    if ($event.columnIndex === $event.sender.columns.length - 1) return;

    // Close any existing editor before opening a new one
    if (this.editedRowIndex !== undefined && this.editedRowIndex !== $event.rowIndex) {
      this.closeEditor($event.sender, this.editedRowIndex);
    }

    // Only create a new form if we don't have one or if we're clicking on a different row
    if (!this.formGroup || this.editedRowIndex !== $event.rowIndex) {
      this.formGroup = this.formService.createContactForm($event.dataItem);
      this.editedRowIndex = $event.rowIndex;
    }

    $event.sender.editRow($event.rowIndex, this.formGroup);
  }

  dragAndDropHandler($event: any) {
    setTimeout(() => {
      const updatedContacts = this.contacts().map((element: any, index: any) => {
        return {
          ...element,
          priority_order: index,
        };
      });
      this.contacts.set(updatedContacts);

      this.updateContacts();
    }, 500);
  }

  getUpdateContactRequests() {
    let requests: Observable<Object>[] = [];
    this.contacts().forEach((contact: any) => {
      contact.organization_id = this.id;
      requests.push(this.ircContactService.updateContact(contact));
    });
    return requests;
  }

  updateContacts() {
    const requests = this.getUpdateContactRequests();
    combineLatest(requests).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  editHandler($event: EditEvent) {
    this.editedRowIndex = $event.rowIndex;
  }

  removeHandler($event: RemoveEvent) {
    const contact = this.contacts()[$event.rowIndex];
    const dialog: DialogRef = this.dialogService.open({
      title: 'Confirm Deletion',
      content: `Are you sure you want to delete the contact "${contact.name}"?`,
      actions: [
        { text: 'No', primary: false },
        { text: 'Yes', primary: true },
      ],
      width: 450,
      height: 200,
    });

    dialog.result.subscribe((result) => {
      if (result instanceof DialogCloseResult) {
        return;
      }

      // Type assertion since we know the structure of our dialog result
      const dialogResult = result as { text: string };
      if (dialogResult.text === 'Yes') {
        const orgId = this.id;
        const pk = contact.pk!;

        this.ircContactService
          .deleteContact(orgId, pk)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.ircContactService
                .getContacts(this.id)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((data) => {
                  this.contacts.set(
                    (data.value || []).sort((a, b) => (a.priority_order ?? 0) - (b.priority_order ?? 0))
                  );
                });
            },
          });
      }
    });
  }

  /**
   * Gets the full name of a day from its value
   */
  public getDayFullName(dayValue: number): string {
    // Adjust for 1-based index from database
    return this.dayFullNames[dayValue - 1] || '';
  }

  /**
   * Gets the 3-letter abbreviation of a day from its value
   */
  public getDayAbbreviation(dayValue: number): string {
    // Adjust for 1-based index from database
    return this.dayAbbreviations[dayValue - 1] || '';
  }
}
